/**
 * Approval gates for scripted runs. The three-agent scenario calls `approvePlan` and `approvePromotion` itself;
 * wrapped here, those calls do not approve anything. They wait until a human approves through the dashboard
 * (seen as `plan.approved` or a `deploy.updated` with `approvedBy` set on the event stream) and then return
 * what the human approved. So in mock mode and in the end-to-end run, the scenario only moves on when the approver clicks.
 */
import { COORDINATOR_OPERATIONS, type Coordinator, type DeployRecord, type PlatformEvent, type Subscription, type WorkPlan } from "@botallion/contracts";

export interface GateNotice {
  kind: "plan" | "promotion";
  /** Spec id or deploy id. */
  id: string;
}

export interface GatedCoordinator extends Coordinator {
  /** What the run is waiting on right now. */
  waiting(): GateNotice[];
  /** Stop waiting (the run is being torn down). Pending gates reject. */
  release(): void;
}

export function gateApprovals(inner: Coordinator, onWait?: (waiting: GateNotice[]) => void): GatedCoordinator {
  const pending = new Map<string, { notice: GateNotice; reject: (e: Error) => void; sub: Subscription }>();
  const report = () => onWait?.([...pending.values()].map((p) => p.notice));

  function waitFor<T>(notice: GateNotice, match: (e: PlatformEvent) => T | undefined): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const key = `${notice.kind}:${notice.id}`;
      let done = false;
      // since: 0 replays history first, so an approval that already happened is seen at once.
      const sub = inner.subscribe({ since: 0, types: notice.kind === "plan" ? ["plan.approved"] : ["deploy.updated"] }, (e) => {
        const hit = match(e);
        if (hit === undefined || done) return;
        done = true;
        pending.delete(key);
        report();
        queueMicrotask(() => sub.close());
        resolve(hit);
      });
      if (!done) {
        pending.set(key, { notice, reject, sub });
        report();
      }
    });
  }

  // Forward every operation, bound to the inner coordinator (a class instance keeps its own state on `this`).
  const forward: Record<string, unknown> = { subscribe: inner.subscribe.bind(inner) };
  for (const op of COORDINATOR_OPERATIONS) forward[op] = (inner[op] as (input?: unknown) => Promise<unknown>).bind(inner);
  const gated = forward as unknown as GatedCoordinator;
  gated.approvePlan = (input) =>
    waitFor<WorkPlan>({ kind: "plan", id: input.specId }, (e) =>
      e.type === "plan.approved" && e.data.plan.specId === input.specId ? e.data.plan : undefined,
    );
  gated.approvePromotion = (input) =>
    waitFor<DeployRecord>({ kind: "promotion", id: input.deployId }, (e) =>
      e.type === "deploy.updated" && e.data.deploy.id === input.deployId && e.data.deploy.approvedBy !== null ? e.data.deploy : undefined,
    );
  gated.waiting = () => [...pending.values()].map((p) => p.notice);
  gated.release = () => {
    for (const p of pending.values()) {
      p.sub.close();
      p.reject(new Error("run stopped"));
    }
    pending.clear();
    report();
  };
  return gated;
}
